import _ from 'lodash';
import moment from 'moment';
import { ComponentProps, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { Callout, Combo, Input, Modal, Separator, Skeleton, Subheading } from '@treams/treams-ui';

import { WarningIcon } from '@treams/treams-icons';
import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import { LocalDate, nativeJs } from '@js-joda/core';

const Label = styled(Subheading)`
	line-height: 34px;
`;

type ComboOnChangeType = ComponentProps<typeof Combo>['onChange'];

export default function AssignCycle(props: {
	user: graphql.User;
	onCancel?: () => void;
	onDismiss?: () => void;
	onAssign: () => Promise<void>;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const [cycle, setCycle] = useState<{ value: string } | undefined>(undefined);

	const [startDate, setStartDate] = useState<Date>(new Date());

	const [assign] = graphql.useCreateCycleInstanceMutation();

	const [fetchCycles, cycles] = graphql.useCycleTemplatesLazyQuery({
		variables: {
			isArchived: false,
			first: 1000,
		},
	});

	const query = graphql.useCycleTemplateQuery({
		variables: { id: cycle ? cycle.value : '' },
		skip: !cycle,
	});

	const selectedCycle = query.data?.cycleTemplate;
	const isCycleAlreadyCompleted =
		selectedCycle &&
		moment(startDate)
			.add(selectedCycle?.duration, 'months')
			.isBefore(moment());

	return (
		<Modal width={400} height="auto">
			<Modal.Header title={t('settings.employees.cycles.assign.title')} />
			<Modal.Contents>
				<Flex alignItems="center">
					<Box flexGrow={1} flexShrink={1} flexBasis="30%">
						<Label>{t('settings.employees.cycles.assign.cycle')}:</Label>
					</Box>
					<Box flexGrow={1} flexShrink={1} flexBasis="70%">
						<Combo
							autoFocus
							custom={false}
							fetch={fetchCycles}
							choice={cycle}
							onChange={setCycle as ComboOnChangeType}
							options={
								cycles.data &&
								_.sortBy(
									cycles.data.cycleTemplates.edges.map(({ node }) => ({
										title: localize(node.title).text,
										value: node.id,
									})),
									'title',
								)
							}
						/>
					</Box>
				</Flex>
				{cycle && (
					<>
						{isCycleAlreadyCompleted && (
							<Callout
								small
								color="yellow"
								icon={<WarningIcon />}
								description={t(
									'settings.employees.cycle.assign.StartDateOutOfCycleWarning.description',
								)}
								mt={2}
							/>
						)}
						<Flex mt={2} alignItems="center">
							<Box flexGrow={1} flexShrink={1} flexBasis="30%">
								<Label>{t('settings.employees.cycles.assign.startDate')}:</Label>
							</Box>
							<Box flexGrow={1} flexShrink={1} flexBasis="70%">
								<Input.DatePicker
									value={startDate}
									onChange={(date) => setStartDate(date ?? new Date())}
									autoFocus
								/>
							</Box>
						</Flex>
						<Box mt={2}>
							<Label>{t('settings.employees.cycles.assign.example.title')}:</Label>
							<Flex>
								<Box flexGrow={1} flexShrink={1} flexBasis="40%">
									<Label>{t('settings.employees.cycles.assign.example.moment')}</Label>
								</Box>
								<Box flexGrow={1} flexShrink={1} flexBasis="30%">
									<Label>{t('settings.employees.cycles.assign.example.startDate')}</Label>
								</Box>
								<Box flexGrow={1} flexShrink={1} flexBasis="30%">
									<Label>{t('settings.employees.cycles.assign.example.endDate')}</Label>
								</Box>
							</Flex>
							<Separator />
							{query?.data?.cycleTemplate.moments.map((momentTemplate) => (
								<Flex key={momentTemplate.id} my={2}>
									<Box flexGrow={1} flexShrink={1} flexBasis="40%">
										{localize(momentTemplate.title).text}
									</Box>
									<Box flexGrow={1} flexShrink={1} flexBasis="30%">
										{moment(startDate).add(momentTemplate.startOffset, 'days').format('DD-MM-YYYY')}
									</Box>
									<Box flexGrow={1} flexShrink={1} flexBasis="30%">
										{moment(startDate)
											.add(momentTemplate.startOffset + momentTemplate.duration, 'days')
											.format('DD-MM-YYYY')}
									</Box>
								</Flex>
							)) ?? (
								<Skeleton.Repeat count={2}>
									<Flex my={2}>
										<Box flexGrow={1} flexShrink={1} flexBasis="40%" pr={3}>
											<Skeleton.Box height={20} />
										</Box>
										<Box flexGrow={1} flexShrink={1} flexBasis="30%" pr={3}>
											<Skeleton.Box height={20} />
										</Box>
										<Box flexGrow={1} flexShrink={1} flexBasis="30%">
											<Skeleton.Box height={20} />
										</Box>
									</Flex>
								</Skeleton.Repeat>
							)}
						</Box>
					</>
				)}
			</Modal.Contents>
			<Modal.FormFooter
				disabled={!cycle || !query.data}
				onCancel={props.onCancel}
				onSave={async () => {
					// Convert the local date to the UTC date, ignorning
					// the timezone differences on purpose.
					const date = LocalDate.from(nativeJs(startDate));

					await assign({
						variables: {
							input: {
								activatesAt: date,
								localStartDate: date,
								cycleTemplateID: cycle!.value,
								userID: props.user.id,
							},
						},
					});
					await props.onAssign();
					props.onDismiss?.();
				}}
			/>
		</Modal>
	);
}
